import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listHappenings } from '@littlebooker/dal';
import { MultiSelectDropdown } from './MultiSelectDropdown';

interface TypeMultiSelectProps {
  value: string[];
  onChange: (types: string[]) => void;
}

// Fallback offered only when the account has no happenings yet.
const CANONICAL_TYPES = ['recording', 'mix', 'show', 'event', 'festival'];

/**
 * Dropdown multi-select of happening types. Options are the distinct types that
 * actually exist in the account's happenings (falling back to the canonical set
 * when there are none), plus anything already selected.
 */
export function TypeMultiSelect({ value, onChange }: TypeMultiSelectProps) {
  const { data: happenings = [], isLoading } = useQuery({
    queryKey: ['happening-types'],
    queryFn: () => listHappenings({ limit: 1000 }),
  });

  const options = useMemo(() => {
    const existing = Array.from(
      new Set(happenings.map((h) => h.type).filter(Boolean) as string[]),
    );
    const base = existing.length > 0 ? existing : CANONICAL_TYPES;
    // Keep any already-selected value visible even if no happening uses it yet.
    return Array.from(new Set([...base, ...value])).sort();
  }, [happenings, value]);

  return (
    <MultiSelectDropdown
      value={value}
      onChange={onChange}
      options={options}
      loading={isLoading}
      placeholder="Select types…"
    />
  );
}
